import { Avatar, TextField } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import { useIsDesktop } from 'Hooks';
import {
  AccordionArrowBlue,
  LeftArrowDatePicker,
  PurpleDateIcon,
  RightArrowDatePicker,
} from 'Images';
import { useController } from 'react-hook-form';
import { requiredMessage } from 'Regex';

interface IDatePickerInput {
  registerName: string;
  label: string;
  maxDate?: Date;
  minDate?: Date;
  maxWidth?: string;
}

export const DatePickerInput = ({
  registerName,
  label,
  maxDate,
  minDate = new Date('1.1.1900'),
  maxWidth,
}: IDatePickerInput) => {
  const isDesktop = useIsDesktop();

  const {
    field,
    fieldState: { error },
  } = useController({
    name: registerName,
    defaultValue: null,
  });

  let { ref, value, onChange, onBlur, ...other } = field;

  const onInputBlur = () => {
    if (
      value.toString() === 'Invalid Date' ||
      (maxDate && value > maxDate) ||
      (minDate && value < minDate)
    )
      onChange(undefined);

    onBlur();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
      <DesktopDatePicker
        inputRef={ref}
        label={label}
        inputFormat='dd/MM/yyyy'
        value={value}
        onChange={onChange}
        maxDate={maxDate}
        minDate={minDate}
        {...other}
        renderInput={(props) => (
          <TextField
            {...props}
            onBlur={onInputBlur}
            error={Boolean(error)}
            helperText={error?.message}
            sx={{
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.shade_200',
                '&:hover': {
                  borderColor: 'primary.shade_200',
                },
              },
              '.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
                borderWidth: '1px',
              },
              '.MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
              width: '100%',
              maxWidth: maxWidth ? maxWidth : isDesktop ? '221px' : '100%',
              flexGrow: 1,
              minWidth: isDesktop ? '400px' : undefined,
            }}
          />
        )}
        InputProps={{
          'aria-required': error?.message === requiredMessage,
          sx: {
            borderRadius: '6px',
            height: isDesktop ? '48px' : '42px',
            mt: isDesktop ? '4px' : '7px',
          },
        }}
        components={{
          OpenPickerIcon: (props) => (
            <Avatar
              {...props}
              src={PurpleDateIcon}
              alt=''
              sx={{
                width: '32px',
                height: '32px',
              }}
            />
          ),
          LeftArrowIcon: (props) => (
            <Avatar
              {...props}
              src={LeftArrowDatePicker}
              alt=''
              sx={{ width: '32px', height: '32px' }}
            />
          ),
          RightArrowIcon: (props) => (
            <Avatar
              {...props}
              src={RightArrowDatePicker}
              alt=''
              sx={{ width: '32px', height: '32px' }}
            />
          ),
          SwitchViewIcon: (props) => (
            <Avatar
              {...props}
              src={AccordionArrowBlue}
              alt=''
              sx={{ width: '28px', height: '28px' }}
            />
          ),
        }}
        PaperProps={{
          sx: {
            borderRadius: '6px',
            width: isDesktop ? '400px' : undefined,
            ml: isDesktop ? '65px' : '27px',
            '.PrivatePickersFadeTransitionGroup-root, div[aria-live="polite"]':
              {
                fontSize: isDesktop ? '1.375rem' : '1.125rem',
                fontWeight: 400,
                lineHeight: isDesktop ? '1.75rem' : '1.5rem',
                color: '#1752AF',
              },
            '.MuiTypography-caption': {
              fontSize: isDesktop ? '1.125rem' : '1rem',
              fontWeight: 700,
              lineHeight: isDesktop ? '1.5rem' : '1.25rem',
              color: '#39546A',
            },
            '.MuiPickersDay-root': {
              fontSize: isDesktop ? '1.125rem' : '1rem',
              fontWeight: 400,
              lineHeight: isDesktop ? '1.625rem' : '1.375rem',
            },
            '.PrivatePickersYear-root > button': {
              fontSize: isDesktop ? '1.125rem' : '1rem',
              fontWeight: 400,
              lineHeight: isDesktop ? '1.625rem' : '1.375rem',
            },
            '.MuiPickersDay-today:not(.Mui-selected)': {
              border: 'none !important',
              backgroundColor: 'white !important',
            },
            '.Mui-selected': {
              color: 'white',
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};
